import {
  EffectOnTargetStatus,
  EngagementType,
  type MeiaCatalogThreatTargetSystemSelectDto,
  type StudySwimlaneSelectDto,
  type StudyTargetEffectOverrideSelectDto,
  type StudyTaskSelectDto,
  type StudyTaskTargetEngagementSelectDto,
  type StudyThreadSelectDto,
} from '@@types/api.types';
import type { BadgeProps } from '@components/ui/badge';
import { formatEnumLabel } from '@utils/meia-enum.util';

export type MeiaPkBand = 'high' | 'low' | 'medium' | 'unknown';

/** Decision-maker attribution for an override: full name when the author profile is loaded, otherwise the raw id. */
export const formatOverrideAuthorName = (override: StudyTargetEffectOverrideSelectDto): string => {
  const profile = override.authorUser?.profile;
  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : '';
  return fullName || override.authorUserId;
};

export type MeiaTargetEffectRow = {
  architectureAlternativeId: string | null;
  decisionMakerOverride?: StudyTargetEffectOverrideSelectDto;
  engagementCount: number;
  maxPkEstimate: number | null;
  modelStatus: EffectOnTargetStatus;
  targetName: string;
  threatTargetSystemCode: string;
};

type BuildTargetEffectRowsParams = {
  architectureAlternativeId: string | null;
  engagements: StudyTaskTargetEngagementSelectDto[];
  overrides: StudyTargetEffectOverrideSelectDto[];
  targetSystems: MeiaCatalogThreatTargetSystemSelectDto[];
};

const effectOnTargetStatusLabels: Record<EffectOnTargetStatus, string> = {
  [EffectOnTargetStatus.CONTESTED]: 'Contested',
  [EffectOnTargetStatus.DEGRADED]: 'Degraded',
  [EffectOnTargetStatus.SUPPRESSED]: 'Suppressed',
  [EffectOnTargetStatus.UNENGAGED]: 'Unengaged',
};

const effectOnTargetStatusDescriptions: Record<EffectOnTargetStatus, string> = {
  [EffectOnTargetStatus.CONTESTED]: 'Contested — the target is engaged but the outcome is uncertain (low pK).',
  [EffectOnTargetStatus.DEGRADED]: 'Degraded — engagements reduce the target’s effectiveness without neutralizing it.',
  [EffectOnTargetStatus.SUPPRESSED]: 'Suppressed — engagements are expected to neutralize the target (high pK).',
  [EffectOnTargetStatus.UNENGAGED]: 'Unengaged — no authored engagement covers this target yet.',
};

export const getEffectOnTargetStatusDescription = (status: EffectOnTargetStatus): string =>
  effectOnTargetStatusDescriptions[status];

/** Combined legend definition for all effect-on-target statuses (one tooltip body). */
export const EFFECT_ON_TARGET_STATUS_LEGEND = Object.values(EffectOnTargetStatus)
  .map((status) => effectOnTargetStatusDescriptions[status])
  .join('\n');

const pkBandBadgeVariantByBand: Record<MeiaPkBand, BadgeProps['variant']> = {
  high: 'success',
  low: 'destructive',
  medium: 'warning',
  unknown: 'secondary',
};

const statusBadgeVariantByStatus: Record<EffectOnTargetStatus, BadgeProps['variant']> = {
  [EffectOnTargetStatus.CONTESTED]: 'warning',
  [EffectOnTargetStatus.DEGRADED]: 'pending',
  [EffectOnTargetStatus.SUPPRESSED]: 'success',
  [EffectOnTargetStatus.UNENGAGED]: 'secondary',
};

export const getEffectOnTargetStatusBadgeVariant = (status: EffectOnTargetStatus): BadgeProps['variant'] =>
  statusBadgeVariantByStatus[status];

export const getEffectOnTargetStatusLabel = (status: EffectOnTargetStatus): string =>
  effectOnTargetStatusLabels[status];

export const getEngagementTypeLabel = (type?: EngagementType | null): string =>
  type ? formatEnumLabel(type) : 'Unspecified';

export const getPkBand = (pkEstimate?: number | null): MeiaPkBand => {
  if (pkEstimate === null || pkEstimate === undefined) return 'unknown';
  if (pkEstimate >= 0.7) return 'high';
  if (pkEstimate >= 0.5) return 'medium';
  return 'low';
};

export const getPkBandBadgeVariant = (pkEstimate?: number | null): BadgeProps['variant'] =>
  pkBandBadgeVariantByBand[getPkBand(pkEstimate)];

export const formatPkEstimate = (pkEstimate?: number | null): string => {
  if (pkEstimate === null || pkEstimate === undefined) return 'pK -';
  return `pK ${Math.round(pkEstimate * 100)}%`;
};

export const deriveEffectOnTargetStatus = (engagements: StudyTaskTargetEngagementSelectDto[]): EffectOnTargetStatus => {
  if (engagements.length === 0) return EffectOnTargetStatus.UNENGAGED;

  const maxPkEstimate = Math.max(...engagements.map((engagement) => engagement.pkEstimate ?? 0));
  if (maxPkEstimate >= 0.7) return EffectOnTargetStatus.SUPPRESSED;
  if (maxPkEstimate >= 0.5) return EffectOnTargetStatus.DEGRADED;
  return EffectOnTargetStatus.CONTESTED;
};

export type MeiaMissionTargetEffectRow = MeiaTargetEffectRow & { missionId: string | null };

type BuildMissionTargetEffectRowsParams = BuildTargetEffectRowsParams & {
  /** taskId → missionId, from {@link buildEngagementMissionIndex}. */
  missionIdByTaskId: Map<string, string>;
};

/** Maps each task id to its mission id via swimlane → thread ownership. */
export const buildEngagementMissionIndex = (params: {
  swimlanes: Array<Pick<StudySwimlaneSelectDto, 'id' | 'threadId'>>;
  tasks: Array<Pick<StudyTaskSelectDto, 'id' | 'swimlaneId'>>;
  threads: Array<Pick<StudyThreadSelectDto, 'id' | 'missionId'>>;
}): Map<string, string> => {
  const { swimlanes, tasks, threads } = params;
  const missionIdByThreadId = new Map(threads.map((thread) => [thread.id, thread.missionId]));
  const threadIdBySwimlaneId = new Map(swimlanes.map((swimlane) => [swimlane.id, swimlane.threadId]));
  const missionIdByTaskId = new Map<string, string>();

  for (const task of tasks) {
    const threadId = threadIdBySwimlaneId.get(task.swimlaneId);
    const missionId = threadId ? missionIdByThreadId.get(threadId) : undefined;
    if (missionId) missionIdByTaskId.set(task.id, missionId);
  }

  return missionIdByTaskId;
};

/**
 * Target-effect rows grouped per mission: engagements roll up by target system
 * within each owning mission, so the same target engaged from two missions
 * yields two rows. Engagements whose mission cannot be resolved group under
 * `missionId: null`.
 */
export const buildMissionTargetEffectRows = (
  params: BuildMissionTargetEffectRowsParams,
): MeiaMissionTargetEffectRow[] => {
  const { engagements, missionIdByTaskId, ...rest } = params;
  const engagementsByMissionId = new Map<string | null, StudyTaskTargetEngagementSelectDto[]>();

  for (const engagement of engagements) {
    const missionId = missionIdByTaskId.get(engagement.taskId) ?? null;
    const current = engagementsByMissionId.get(missionId) ?? [];
    current.push(engagement);
    engagementsByMissionId.set(missionId, current);
  }

  return Array.from(engagementsByMissionId.entries()).flatMap(([missionId, missionEngagements]) =>
    buildTargetEffectRows({ ...rest, engagements: missionEngagements }).map((row) => ({ ...row, missionId })),
  );
};

export const buildTargetEffectRows = (params: BuildTargetEffectRowsParams): MeiaTargetEffectRow[] => {
  const { architectureAlternativeId, engagements, overrides, targetSystems } = params;
  const targetSystemByCode = new Map(targetSystems.map((targetSystem) => [targetSystem.id, targetSystem]));
  const engagementsByTargetCode = new Map<string, StudyTaskTargetEngagementSelectDto[]>();

  for (const engagement of engagements) {
    const current = engagementsByTargetCode.get(engagement.threatTargetSystemCode) ?? [];
    current.push(engagement);
    engagementsByTargetCode.set(engagement.threatTargetSystemCode, current);
  }

  return Array.from(engagementsByTargetCode.entries())
    .map(([threatTargetSystemCode, targetEngagements]) => {
      const targetSystem = targetSystemByCode.get(threatTargetSystemCode);
      const maxPkEstimate = Math.max(...targetEngagements.map((engagement) => engagement.pkEstimate ?? 0));
      const decisionMakerOverride = overrides.find(
        (override) =>
          override.threatTargetSystemCode === threatTargetSystemCode &&
          (override.architectureAlternativeId ?? null) === architectureAlternativeId,
      );

      const hasPkEstimate = targetEngagements.some(
        (engagement) => engagement.pkEstimate !== null && engagement.pkEstimate !== undefined,
      );

      return {
        architectureAlternativeId,
        decisionMakerOverride,
        engagementCount: targetEngagements.length,
        maxPkEstimate: hasPkEstimate ? maxPkEstimate : null,
        modelStatus: deriveEffectOnTargetStatus(targetEngagements),
        targetName: targetSystem?.name ?? threatTargetSystemCode,
        threatTargetSystemCode,
      };
    })
    .sort((first, second) => first.threatTargetSystemCode.localeCompare(second.threatTargetSystemCode));
};
